// „Twoje linie" (SPEC-redesign §5.7, catalogue 01, 05–07). The resident's own
// lines as inverted chips, and every line that is on the road right now as a
// grid to pick from — square for a tram, pill for a bus, inverted when it is
// already theirs.
//
// The grid is derived from the snapshot rather than from a second endpoint:
// `/api/lines` is built by `server/ztm.ts` out of exactly the vehicle snapshot
// that `/api/vehicles` serves, so the two lists are the same list, and the data
// seam stays the one place a wire shape is read (§4.3).
import type { Snapshot } from '../data/data.ts';
import { freshness } from '../derive/freshness.ts';
import { clockLabel } from '../shared/format.ts';
import type { Item } from '../shared/model.ts';
import { viewState, type ViewState } from '../state/viewstate.ts';

/** `null` while the seam does not yet say which it is — see `modeOf`. */
export type LineOnRoute = { line: string; type: 'tram' | 'bus' | null; count: number };
export type LineFilter = 'all' | 'tram' | 'bus';

/**
 * Tram or bus is **data**, never presentation. Reading it off the `▢`/`◯` glyph
 * would be the coupling §4.5 rules out for `sub.tone`: restyle the glyph and
 * this screen would silently start calling trams buses.
 *
 * A vehicle the seam gave no `mode` for is of **no** kind rather than of a
 * guessed one — it takes the neutral square and matches „Wszystkie" alone.
 */
const modeOf = (item: Item): 'tram' | 'bus' | null => item.mode ?? null;

/** The prototype's three, with the label each one wears. */
const FILTERS: readonly { id: LineFilter; label: string }[] = [
  { id: 'all', label: 'Wszystkie' },
  { id: 'tram', label: 'Tramwaje' },
  { id: 'bus', label: 'Autobusy' },
];

/**
 * Every line with a vehicle on the road, in the order the server used: by
 * number, and by name where the number is a tie (`server/ztm.ts:171`). The
 * resident's own lines are **not** sorted this way — those keep the order they
 * were added in (§4.2) — so the two orders are deliberately different.
 */
export function linesOnRoute(items: readonly Item[]): LineOnRoute[] {
  const byLine = new Map<string, LineOnRoute>();
  for (const item of items) {
    if (item.kind !== 'vehicle') continue;
    const line = item.lines?.[0];
    if (!line) continue;
    const entry = byLine.get(line) ?? { line, type: modeOf(item), count: 0 };
    entry.count += 1;
    byLine.set(line, entry);
  }
  return [...byLine.values()].toSorted(
    (a, b) => Number(a.line) - Number(b.line) || a.line.localeCompare(b.line),
  );
}

/**
 * What the grid shows for one filter and one query. The query is a prefix, as in
 * the prototype: typing `1` keeps 12, 13, 16, 18, 163, 174, 190 and drops 2.
 * Neither the filter nor the query is in the address — they are a way of looking
 * through a list, not a view worth sharing (§9.8 of the dossier).
 */
export function filterLines(lines: readonly LineOnRoute[], filter: LineFilter, query: string): LineOnRoute[] {
  const q = query.trim();
  return lines.filter((l) => (filter === 'all' || l.type === filter) && (q === '' || l.line.startsWith(q)));
}

/** `1 linia`, `2 linie`, `13 linii` — the prototype's own plural rule (§7.2). */
export function liniiLabel(n: number): string {
  if (n === 1) return '1 linia';
  const last = n % 10;
  const teens = n % 100;
  const few = last >= 2 && last <= 4 && !(teens >= 12 && teens <= 14);
  return `${n} ${few ? 'linie' : 'linii'}`;
}

/**
 * What the header says about the vehicle feed. The kreska shows the same
 * reading, and the two may not disagree — so there is one reading and this
 * screen wears it (#218). The second half of the sentence is this screen's own:
 * the header has room for the seconds the kreska keeps behind `aria-hidden`.
 */
export function freshnessLabel(snapshot: Snapshot, now: number): string {
  const reading = freshness(snapshot, now);
  if (reading.kind === 'live') return `na żywo, ${reading.short} temu`;
  // `ageing` and `stale` both print the source's own hour; what separates them
  // is the note below, which names the source when it stopped answering.
  if (reading.kind === 'ageing' || reading.kind === 'stale') return `dane z ${reading.short}`;
  return reading.sentence;
}

/**
 * The note that stands where the grid would be. A starting source and a dead one
 * must never read the same (§7.2, D-2), and a source that never answered must not
 * offer „ostatnią" — there is no last list to show. With a list on screen the
 * note dates it instead of calling the refresh failed (Decision 37, #83).
 */
export function pickerNote(snapshot: Snapshot, hasLines: boolean): string | null {
  if (!snapshot.serverReachable) return 'brak połączenia z serwerem';
  const { state, updatedAt } = snapshot.health.ztm;
  if (state === 'starting') return 'łączę się…';
  if (state === 'down') {
    return hasLines && updatedAt
      ? `lista z ${clockLabel(updatedAt)}, ZTM nie odpowiada`
      : 'ZTM nie odpowiada. Nie mamy jeszcze listy linii.';
  }
  return null;
}

/** Adding and removing are `'replace'` writes, so they survive `‹`, Esc and Back (D-b). */
function toggleLine(line: string, view: ViewState) {
  const lines = view.lines.includes(line)
    ? view.lines.filter((l) => l !== line)
    : // the order they were added in, never the numeric order (§4.2)
      [...view.lines, line];
  viewState.set({ lines }, 'replace');
}

/**
 * A chosen line, in the order it was added. It wears the same shape as its tile
 * — square for a tram, pill for a bus — so a resident recognises it twice. A
 * line that is not on the road right now has no shape we know of, and takes the
 * tram's: the chip must still be removable when nothing of it is running (§3.1,
 * catalogue 08).
 */
function chip(line: string, type: 'tram' | 'bus' | null, view: ViewState): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = `picker__chip picker__chip--${type ?? 'tram'}`;
  b.setAttribute('aria-label', `Linia ${line}, usuń z moich`);
  b.append(line);
  const cross = document.createElement('span');
  cross.textContent = '✕';
  cross.setAttribute('aria-hidden', 'true');
  b.append(cross);
  b.addEventListener('click', () => {
    toggleLine(line, view);
  });
  return b;
}

function tile(entry: LineOnRoute, view: ViewState): HTMLButtonElement {
  const mine = view.lines.includes(entry.line);
  const b = document.createElement('button');
  b.type = 'button';
  // square = tram, pill = bus; inverted = already mine. The shape carries the
  // kind and the inversion carries the choice, so colour is never the only
  // signal (§8).
  b.className = `picker__tile picker__tile--${entry.type ?? 'tram'}${mine ? ' picker__tile--mine' : ''}`;
  b.setAttribute('aria-label', `Linia ${entry.line}, ${mine ? 'usuń z moich' : 'dodaj do moich'}`);
  b.setAttribute('aria-pressed', String(mine));
  b.textContent = entry.line;
  b.addEventListener('click', () => {
    toggleLine(entry.line, view);
  });
  return b;
}

/**
 * The screen's body. The `‹ Twoje linie` header belongs to the overlay host,
 * which is also what re-renders this on every change — the filter and the query
 * are the only state this screen keeps to itself.
 */
export function pickerScreen(snapshot: Snapshot, view: ViewState, state: PickerState): HTMLElement {
  const body = document.createElement('div');
  body.className = 'picker';

  const all = linesOnRoute(snapshot.items);
  const shapeOf = new Map(all.map((l) => [l.line, l.type]));

  // my lines, in the order they were added
  if (view.lines.length > 0) {
    const mine = document.createElement('div');
    mine.className = 'picker__mine';
    for (const line of view.lines) mine.append(chip(line, shapeOf.get(line) ?? null, view));
    body.append(mine);
  } else {
    const empty = document.createElement('p');
    empty.className = 'picker__intro';
    empty.textContent = 'Wybierz linie, którymi jeździsz. Zapamiętamy je w tej przeglądarce.';
    body.append(empty);
  }

  const note = pickerNote(snapshot, all.length > 0);
  const shown = filterLines(all, state.filter, state.query);

  const head = document.createElement('div');
  head.className = 'picker__head';
  const heading = document.createElement('h3');
  heading.className = 'picker__heading';
  heading.textContent = 'Na trasie teraz';
  const count = document.createElement('span');
  count.className = 'picker__count';
  count.textContent = liniiLabel(shown.length);
  head.append(heading, count);
  body.append(head);

  const filters = document.createElement('div');
  filters.className = 'picker__filters';
  for (const f of FILTERS) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `picker__filter${state.filter === f.id ? ' picker__filter--on' : ''}`;
    b.setAttribute('aria-pressed', String(state.filter === f.id));
    b.textContent = f.label;
    b.addEventListener('click', () => {
      state.filter = f.id;
      state.onChange();
    });
    filters.append(b);
  }
  body.append(filters);

  const field = document.createElement('input');
  field.className = 'picker__field';
  field.type = 'text';
  field.inputMode = 'numeric';
  field.maxLength = 3;
  field.value = state.query;
  field.placeholder = 'Wpisz numer linii';
  field.setAttribute('aria-label', 'Wpisz numer linii');
  field.addEventListener('input', () => {
    // digits only, as in the prototype: a line number is a number
    state.query = field.value.replace(/\D/g, '').slice(0, 3);
    state.onChange();
  });
  body.append(field);

  const say = (text: string) => {
    const p = document.createElement('p');
    // a failure is read as a failure, so it is announced rather than waited for
    p.className = 'picker__note';
    p.setAttribute('role', 'status');
    p.textContent = text;
    body.append(p);
  };

  // The note and the grid are **independent**. A source that went down after one
  // good answer says „Pokazujemy ostatnią." and then shows that last list —
  // suppressing the grid would make the sentence a lie, and it is the common ZTM
  // failure, not the cold one.
  if (note) say(note);

  if (shown.length > 0) {
    const grid = document.createElement('div');
    grid.className = 'picker__grid';
    for (const entry of shown) grid.append(tile(entry, view));
    body.append(grid);
  } else if (all.length > 0 || !note) {
    // Nothing matches, and there was something to match against — the filter or
    // the number emptied the grid. When a note is up and the catalogue is empty
    // the note has already said why, and „Żadna taka linia…" would blame the
    // resident's query for a source being down.
    say('Żadna taka linia nie jest teraz na trasie.');
  }

  const foot = document.createElement('p');
  foot.className = 'picker__foot';
  foot.textContent =
    'Dotknij linii, żeby dodać ją do swoich. Zapamiętane linie widzisz na mapie z numerem i kierunkiem jazdy, pozostałe jako kropki.';
  body.append(foot);

  return body;
}

/** The filter and the query, which are this screen's alone and are not in the address. */
export type PickerState = { filter: LineFilter; query: string; onChange: () => void };
